import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
    LogOut, Bell, Menu, X, Search, Command, Home,
    LayoutDashboard, PenTool, Users, Wrench, Calendar, Route, Package,
    BarChart3, DollarSign, MessageSquare, Shield, Zap, FileText, Settings, Mail
} from 'lucide-react';
import { Button } from '../../../components/Button';
import { NavDropdown, NavButton } from './NavDropdown';

interface NavbarProps {
    role: string;
    onLogout: () => void;
    badges?: {
        applications?: number;
        pendingLeases?: number;
        installations?: number;
        openTickets?: number;
        lowStock?: number;
        todayRoutes?: number;
    };
    // Super Admin view switching
    viewAs?: 'SUPER_ADMIN' | 'ADMIN';
    setViewAs?: (view: 'SUPER_ADMIN' | 'ADMIN') => void;
}

export const Navbar: React.FC<NavbarProps> = ({ role, onLogout, badges = {}, viewAs, setViewAs }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const isAdmin = role === 'ADMIN' || role === 'STAFF' || role === 'SUPER_ADMIN';
    const isSuperAdmin = role === 'SUPER_ADMIN';

    // Quick search items - appliance focused
    const searchableItems = [
        { label: 'Command Center', path: '/admin', icon: <LayoutDashboard size={16} /> },
        { label: 'Analytics', path: '/admin/analytics', icon: <BarChart3 size={16} /> },
        { label: 'Applications', path: '/admin/applications', icon: <PenTool size={16} /> },
        { label: 'Customers', path: '/admin/customers', icon: <Users size={16} /> },
        { label: 'Installations', path: '/admin/installations', icon: <Wrench size={16} /> },
        { label: 'Scheduler', path: '/admin/maintenance', icon: <Calendar size={16} /> },
        { label: 'Routes', path: '/admin/routes', icon: <Route size={16} /> },
        { label: 'Inventory', path: '/admin/inventory', icon: <Package size={16} /> },
        { label: 'Cashflow', path: '/admin/cashflow', icon: <DollarSign size={16} /> },
        { label: 'Financials', path: '/admin/financials', icon: <DollarSign size={16} /> },
        { label: 'Messages', path: '/admin/messages', icon: <MessageSquare size={16} /> },
        { label: 'Contact Messages', path: '/admin/contact-messages', icon: <Mail size={16} /> },
        { label: 'Support', path: '/admin/tickets', icon: <MessageSquare size={16} /> },
        { label: 'Notifications', path: '/admin/notifications', icon: <Bell size={16} /> },
        { label: 'Staff', path: '/admin/staff', icon: <Shield size={16} /> },
        { label: 'Settings', path: '/admin/settings', icon: <Settings size={16} /> },
    ];

    const filteredSearchItems = searchQuery
        ? searchableItems.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
        : searchableItems;

    // ⌘+K keyboard shortcut
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setShowSearch(prev => !prev);
                setSearchQuery('');
            }
            if (e.key === 'Escape') setShowSearch(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleSearchSelect = (path: string) => {
        navigate(path);
        setShowSearch(false);
        setSearchQuery('');
    };

    // 🔌 APPLIANCE RENTALS (Primary business)
    const applianceRentalItems = [
        { label: 'Inventory', path: '/admin/inventory', icon: <Package size={16} />, badge: badges.lowStock },
        { label: 'Customers', path: '/admin/customers', icon: <Users size={16} />, badge: badges.pendingLeases },
        { label: 'Applications', path: '/admin/applications', icon: <PenTool size={16} />, badge: badges.applications },
        { label: 'Installations', path: '/admin/installations', icon: <Wrench size={16} />, badge: badges.installations },
        { label: 'Scheduler', path: '/admin/maintenance', icon: <Calendar size={16} /> },
        { label: 'Routes', path: '/admin/routes', icon: <Route size={16} />, badge: badges.todayRoutes },
    ];

    // 💰 FINANCE
    const financeItems = [
        { label: 'Cashflow', path: '/admin/cashflow', icon: <DollarSign size={16} /> },
        { label: 'Transactions', path: '/admin/financials', icon: <DollarSign size={16} /> },
        { label: 'Analytics', path: '/admin/analytics', icon: <BarChart3 size={16} /> },
    ];

    // ⚙️ ADMIN
    const adminItems = [
        { label: 'Messages', path: '/admin/messages', icon: <MessageSquare size={16} /> },
        { label: 'Contact Messages', path: '/admin/contact-messages', icon: <Mail size={16} /> },
        { label: 'Support', path: '/admin/tickets', icon: <MessageSquare size={16} />, badge: badges.openTickets },
        { label: 'Notifications', path: '/admin/notifications', icon: <Bell size={16} /> },
        { label: 'Staff', path: '/admin/staff', icon: <Shield size={16} /> },
        { label: 'Settings', path: '/admin/settings', icon: <Settings size={16} /> },
    ];

    return (
        <>
            {/* Quick Search Modal */}
            {showSearch && (
                <div className="fixed inset-0 bg-black/70 z-[100] flex items-start justify-center pt-24 px-4 backdrop-blur-sm">
                    <div className="bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
                            <Search size={18} className="text-slate-400" />
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search pages..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="flex-1 bg-transparent border-none outline-none text-slate-900 placeholder-slate-400"
                            />
                            <button onClick={() => setShowSearch(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="max-h-80 overflow-y-auto py-2">
                            {filteredSearchItems.map(item => (
                                <button
                                    key={item.path}
                                    onClick={() => handleSearchSelect(item.path)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                                >
                                    <span className="text-slate-400">{item.icon}</span>
                                    <span className="font-medium">{item.label}</span>
                                    {location.pathname === item.path && (
                                        <span className="ml-auto text-xs text-brand-600 font-medium">Current</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="px-4 py-2 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-400">
                            <Command size={12} />
                            <span>Press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">ESC</kbd> to close</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Navbar */}
            <nav className="glass fixed top-0 left-0 right-0 h-16 px-4 md:px-6 flex justify-between items-center z-50 transition-all duration-300 shadow-sm border-b border-slate-200/50">
                {/* Left: Logo + Nav */}
                <div className="flex items-center gap-4">
                    {/* Logo */}
                    <div
                        className="flex items-center gap-3 cursor-pointer group"
                        onClick={() => navigate(isAdmin ? '/admin' : role === 'CUSTOMER' ? '/dashboard' : '/')}
                    >
                        <div className="w-9 h-9 bg-gradient-to-br from-brand-600 to-indigo-600 rounded-xl text-white flex items-center justify-center font-bold shadow-lg shadow-brand-500/30 group-hover:scale-105 transition-transform">N</div>
                        <span className="font-bold text-xl hidden sm:block text-slate-800 tracking-tight group-hover:text-brand-600 transition-colors">Nestly</span>
                    </div>

                    {/* Home Link */}
                    <button
                        onClick={() => navigate(isAdmin ? '/admin' : role === 'CUSTOMER' ? '/dashboard' : '/')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors ml-2"
                        title="Dashboard Home"
                    >
                        <Home size={16} />
                        <span className="hidden lg:inline">Home</span>
                    </button>

                    {/* Desktop Navigation - Appliance Only */}
                    {isAdmin && (
                        <div className="hidden md:flex items-center gap-1 ml-4 pl-4 border-l border-slate-200">
                            <NavButton to="/admin" icon={<Zap size={16} />} label="Command" />
                            <NavDropdown
                                label="Rentals"
                                items={applianceRentalItems}
                                badgeCount={(badges.applications || 0) + (badges.pendingLeases || 0) + (badges.installations || 0) + (badges.lowStock || 0)}
                            />
                            <NavDropdown label="Finance" items={financeItems} />
                            <NavDropdown
                                label="Admin"
                                items={adminItems}
                                badgeCount={badges.openTickets}
                            />
                        </div>
                    )}
                </div>

                {/* Right: Actions */}
                <div className="flex gap-2 items-center">
                    {/* Search Button */}
                    {isAdmin && (
                        <button
                            onClick={() => setShowSearch(true)}
                            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                        >
                            <Search size={14} />
                            <span className="text-xs">Search</span>
                            <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 bg-slate-200 rounded text-slate-400">⌘K</kbd>
                        </button>
                    )}

                    {/* Super Admin View Switcher */}
                    {isSuperAdmin && setViewAs && (
                        <button
                            onClick={() => setViewAs(viewAs === 'SUPER_ADMIN' ? 'ADMIN' : 'SUPER_ADMIN')}
                            className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-all ${viewAs === 'ADMIN'
                                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 ring-2 ring-orange-400'
                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                }`}
                            title={viewAs === 'ADMIN' ? 'Switch to Platform View' : 'Switch to Company View'}
                        >
                            {viewAs === 'ADMIN' ? (
                                <>
                                    <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                                    <span>Company View</span>
                                </>
                            ) : (
                                <>
                                    <Shield size={14} />
                                    <span>Platform</span>
                                </>
                            )}
                        </button>
                    )}

                    {role !== 'GUEST' && (
                        <div className="flex items-center gap-2 md:gap-3">
                            {/* Mobile Menu Toggle */}
                            {isAdmin && (
                                <button
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    className="md:hidden p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                                </button>
                            )}

                            {/* Notification Bell */}
                            <button
                                onClick={() => navigate('/admin/notifications')}
                                className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <Bell size={20} />
                                {(badges.openTickets || 0) > 0 && (
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                                )}
                            </button>

                            {/* User Avatar */}
                            <div className="flex items-center gap-2 pl-2 md:pl-3 border-l border-slate-200">
                                <div className="w-8 h-8 bg-gradient-to-br from-slate-700 to-slate-900 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                    {role === 'ADMIN' ? 'AD' : role === 'STAFF' ? 'ST' : 'CU'}
                                </div>
                            </div>

                            {/* Logout */}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={onLogout}
                                className="flex items-center gap-2 hover:bg-red-50 hover:text-red-600 transition-colors"
                            >
                                <LogOut size={14} />
                                <span className="hidden md:inline">Logout</span>
                            </Button>
                        </div>
                    )}
                </div>
            </nav>

            {/* Mobile Menu Drawer - Appliance Only */}
            {mobileMenuOpen && isAdmin && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileMenuOpen(false)} />
                    <div className="fixed top-16 left-0 right-0 bg-white z-40 md:hidden border-b border-slate-200 shadow-xl max-h-[80vh] overflow-y-auto">
                        <div className="p-4 space-y-4">
                            <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
                                <Zap size={18} className="text-brand-600" />
                                <span className="font-medium">Command Center</span>
                            </Link>

                            <div className="border-t border-slate-100 pt-4">
                                <p className="text-xs font-bold text-slate-400 uppercase mb-2 px-3">🔌 Appliance Rentals</p>
                                {applianceRentalItems.map(item => (
                                    <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
                                        {item.icon}
                                        <span>{item.label}</span>
                                        {item.badge ? <span className="ml-auto bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{item.badge}</span> : null}
                                    </Link>
                                ))}
                            </div>

                            <div className="border-t border-slate-100 pt-4">
                                <p className="text-xs font-bold text-slate-400 uppercase mb-2 px-3">Finance & Admin</p>
                                {[...financeItems, ...adminItems].map(item => {
                                    const badge = (item as { badge?: number }).badge;
                                    return (
                                        <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
                                            {item.icon}
                                            <span>{item.label}</span>
                                            {badge ? <span className="ml-auto bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{badge}</span> : null}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
